import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

export default function ProfilePage() {
  const { user: sessionUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Per-account credential editing
  const [acctEditing, setAcctEditing] = useState(null); // acct username being edited
  const [acctDraft, setAcctDraft] = useState({});
  const [acctSaving, setAcctSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({}); // {acct_username: bool}

  // Pause / Resume
  const [pausing, setPausing] = useState({}); // {acct_id: bool}
  const togglePause = async (acct) => {
    setPausing(p => ({ ...p, [acct.id]: true }));
    try {
      const res = await axios.post(`/settings/accounts/${acct.id}/pause`);
      setProfile(p => ({
        ...p,
        accounts: p.accounts.map(a =>
          a.id === acct.id ? { ...a, is_paused: res.data.is_paused ? 1 : 0 } : a
        )
      }));
      flash(res.data.is_paused ? `${acct.username} paused.` : `${acct.username} resumed.`);
    } catch {
      flash("Failed to update account status.", false);
    } finally {
      setPausing(p => ({ ...p, [acct.id]: false }));
    }
  };

  // Delete account
  const [deleting, setDeleting] = useState({}); // {acct_id: bool}
  const deleteAccount = async (acct) => {
    if (!window.confirm(`Delete account ${acct.username}? This cannot be undone.`)) return;
    setDeleting(d => ({ ...d, [acct.id]: true }));
    try {
      await axios.delete(`/settings/accounts/${acct.id}`);
      setProfile(p => ({ ...p, accounts: p.accounts.filter(a => a.id !== acct.id) }));
      flash(`${acct.username} deleted.`);
    } catch {
      flash("Failed to delete account.", false);
    } finally {
      setDeleting(d => ({ ...d, [acct.id]: false }));
    }
  };

  // Test broker connection
  const [testing, setTesting] = useState({}); // {acct_id: true/false}
  const testConnection = async (acctId) => {
    setTesting(t => ({ ...t, [acctId]: "loading" }));
    try {
      const res = await axios.post(`/settings/accounts/${acctId}/test`);
      setTesting(t => ({ ...t, [acctId]: res.data.ok ? "ok" : "fail" }));
      flash(res.data.ok ? (res.data.message || "Connection successful!") : (res.data.error || "Connection failed."), res.data.ok);
    } catch (e) {
      setTesting(t => ({ ...t, [acctId]: "fail" }));
      flash(e?.response?.data?.error || "Connection test failed.", false);
    }
    setTimeout(() => setTesting(t => { const n = { ...t }; delete n[acctId]; return n; }), 5000);
  };

  // Add new broker account
  const [showAddForm, setShowAddForm] = useState(false);
  const US_BROKERS = ["alpaca", "tradier", "ibkr"];
  const EMPTY_NEW = { username: "", password: "", broker: "angelone", api_key: "", totp_token: "", market: "IN" };
  const [newAcct, setNewAcct] = useState(EMPTY_NEW);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get("/settings/profile");
      setProfile(res.data);
      setDraft({
        firstname: res.data.firstname,
        lastname: res.data.lastname,
        validity_date: res.data.validity_date,
      });
    } catch {
      setProfile(null);
    }
  };

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await axios.put("/settings/profile", draft);
      setProfile(p => ({ ...p, ...draft }));
      setEditing(false);
      flash("Profile saved.");
    } catch {
      flash("Failed to save profile.", false);
    } finally {
      setSaving(false);
    }
  };

  const addAccount = async () => {
    setAddError(null);
    if (!newAcct.username || !newAcct.password || !newAcct.broker) {
      setAddError("Username, password and broker are required.");
      return;
    }
    try {
      setAddSaving(true);
      await axios.post("/settings/accounts", newAcct);
      flash("Broker account added.");
      setShowAddForm(false);
      setNewAcct(EMPTY_NEW);
      fetchProfile();
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to add account.";
      setAddError(msg);
      flash(msg, false);
    } finally {
      setAddSaving(false);
    }
  };

  const saveAcct = async (acctUsername) => {
    try {
      setAcctSaving(true);
      await axios.put("/settings/profile", {
        acct_username: acctUsername,
        ...acctDraft
      });
      setProfile(p => ({
        ...p,
        accounts: p.accounts.map(a =>
          a.username === acctUsername ? { ...a, ...acctDraft } : a
        )
      }));
      setAcctEditing(null);
      flash("Broker credentials saved.");
    } catch {
      flash("Failed to save credentials.", false);
    } finally {
      setAcctSaving(false);
    }
  };

  if (!profile) return (
    <div style={{ padding: 25 }}>
      <p style={{ color: "#888" }}>Loading profile…</p>
    </div>
  );

  return (
    <div style={{ padding: 25, background: "#f4f6f9", minHeight: "100vh" }}>

      <h2 style={pageTitle}>User Profile</h2>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text}
        </div>
      )}

      {/* ── ACCOUNT SUMMARY TILES ── */}
      <div style={summaryGrid}>
        <SummaryTile label="Username"       value={profile.username}       color="#007bff" />
        <SummaryTile label="Name"           value={`${profile.firstname || ""} ${profile.lastname || ""}`.trim() || "—"} color="#6f42c1" />
        <SummaryTile label="Subscription"   value={(profile.subscription_plan || "retail").toUpperCase()} color="#28a745" />
        <SummaryTile label="Broker Accounts" value={`${(profile.accounts || []).length} / ${profile.subscription_limit || 1}`} color="#17a2b8" />
        <SummaryTile label="Valid Until"    value={profile.validity_date}  color="#fd7e14" />
      </div>

      {/* ── PERSONAL INFO ── */}
      <Card title="Personal Information">
        <div style={grid2}>
          <Field label="First Name" editing={editing}
            value={draft.firstname ?? ""} display={profile.firstname}
            onChange={(v) => setDraft(d => ({ ...d, firstname: v }))} />
          <Field label="Last Name" editing={editing}
            value={draft.lastname ?? ""} display={profile.lastname}
            onChange={(v) => setDraft(d => ({ ...d, lastname: v }))} />
          <Field label="Username" editing={false}
            value={profile.username} display={profile.username}
            onChange={() => {}} />
          <Field label="Validity Date" editing={editing}
            type="date"
            value={draft.validity_date ?? ""} display={profile.validity_date}
            onChange={(v) => setDraft(d => ({ ...d, validity_date: v }))} />
        </div>

        <ActionRow editing={editing} saving={saving}
          onEdit={() => setEditing(true)} onSave={saveProfile}
          onCancel={() => { setEditing(false); }} />
      </Card>

      {/* ── TRADING ACCOUNTS ── */}
      <Card title="Trading Accounts &amp; Broker Credentials">
        {(!profile.accounts || profile.accounts.length === 0) ? (
          <p style={{ color: "#888", fontSize: 13 }}>No trading accounts found.</p>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                {["Account", "Status", "Broker", "Market", "API Key", "TOTP Token", "Actions"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profile.accounts.map(acct => {
                const isEditing = acctEditing === acct.username;
                const showKey = showSecrets[acct.username + "_key"];
                const showTotp = showSecrets[acct.username + "_totp"];

                return (
                  <tr key={acct.username} style={{ ...tr, opacity: acct.is_paused ? 0.6 : 1 }}>
                    <td style={td}><strong>{acct.username}</strong></td>
                    <td style={td}>
                      <span style={{
                        ...badge,
                        background: acct.is_paused ? "#fff3cd" : "#d4edda",
                        color: acct.is_paused ? "#856404" : "#155724"
                      }}>
                        {acct.is_paused ? "Paused" : "Active"}
                      </span>
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <select value={acctDraft.broker ?? acct.broker}
                          onChange={(e) => setAcctDraft(d => ({ ...d, broker: e.target.value }))}
                          style={{ ...input, width: "auto" }}>
                          {["angelone", "zerodha", "upstox", "alpaca", "tradier", "ibkr", "other"].map(b => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                      ) : (
                        <span style={badge}>{acct.broker}</span>
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <select value={acctDraft.market ?? acct.market ?? "IN"}
                          onChange={(e) => setAcctDraft(d => ({ ...d, market: e.target.value }))}
                          style={{ ...input, width: "auto" }}>
                          <option value="IN">IN (India)</option>
                          <option value="US">US (United States)</option>
                        </select>
                      ) : (
                        <span style={{ ...badge, background: (acct.market || "IN") === "US" ? "#e8f4fd" : "#fff3cd", color: (acct.market || "IN") === "US" ? "#0070c9" : "#856404" }}>
                          {acct.market || "IN"}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <input type="text"
                          value={acctDraft.api_key ?? acct.api_key ?? ""}
                          onChange={(e) => setAcctDraft(d => ({ ...d, api_key: e.target.value }))}
                          style={{ ...input, fontFamily: "monospace", fontSize: 12 }} />
                      ) : (
                        <SecretCell value={acct.api_key} show={showKey}
                          onToggle={() => setShowSecrets(s => ({ ...s, [acct.username + "_key"]: !showKey }))} />
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <input type="text"
                          value={acctDraft.totp_token ?? acct.totp_token ?? ""}
                          onChange={(e) => setAcctDraft(d => ({ ...d, totp_token: e.target.value }))}
                          style={{ ...input, fontFamily: "monospace", fontSize: 12 }} />
                      ) : (
                        <SecretCell value={acct.totp_token} show={showTotp}
                          onToggle={() => setShowSecrets(s => ({ ...s, [acct.username + "_totp"]: !showTotp }))} />
                      )}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => saveAcct(acct.username)}
                            disabled={acctSaving}
                            style={btnSmallPrimary}>
                            {acctSaving ? "…" : "Save"}
                          </button>
                          <button onClick={() => setAcctEditing(null)} style={btnSmallSecondary}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            onClick={() => {
                              setAcctEditing(acct.username);
                              setAcctDraft({ broker: acct.broker, api_key: acct.api_key, totp_token: acct.totp_token, market: acct.market || "IN" });
                            }}
                            style={btnSmallSecondary}>
                            Edit
                          </button>
                          <button
                            onClick={() => testConnection(acct.id)}
                            disabled={testing[acct.id] === "loading"}
                            style={{
                              ...btnSmallSecondary,
                              color: testing[acct.id] === "ok" ? "#28a745" : testing[acct.id] === "fail" ? "#dc3545" : "#6f42c1",
                              borderColor: testing[acct.id] === "ok" ? "#28a745" : testing[acct.id] === "fail" ? "#dc3545" : "#6f42c1",
                            }}>
                            {testing[acct.id] === "loading" ? "Testing…" : testing[acct.id] === "ok" ? "✓ OK" : testing[acct.id] === "fail" ? "✗ Fail" : "Test"}
                          </button>
                          <button
                            onClick={() => togglePause(acct)}
                            disabled={pausing[acct.id]}
                            style={{
                              ...btnSmallSecondary,
                              color: acct.is_paused ? "#28a745" : "#fd7e14",
                              borderColor: acct.is_paused ? "#28a745" : "#fd7e14",
                            }}>
                            {pausing[acct.id] ? "…" : acct.is_paused ? "Resume" : "Pause"}
                          </button>
                          <button
                            onClick={() => deleteAccount(acct)}
                            disabled={deleting[acct.id]}
                            style={{ ...btnSmallSecondary, color: "#dc3545", borderColor: "#dc3545" }}>
                            {deleting[acct.id] ? "…" : "Delete"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ── ADD NEW ACCOUNT ── */}
        <div style={{ marginTop: 20, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
          {!showAddForm ? (
            (profile.accounts || []).length >= (profile.subscription_limit || 1) ? (
              <div style={{ fontSize: 13, color: "#888" }}>
                Broker account limit reached for your <strong>{profile.subscription_plan || "retail"}</strong> plan
                ({profile.subscription_limit || 1} account{profile.subscription_limit > 1 ? "s" : ""}).
                Contact admin to upgrade.
              </div>
            ) : (
            <button onClick={() => setShowAddForm(true)} style={btnAddAccount}>
              + Add Broker Account
            </button>
            )
          ) : (
            <div style={addFormBox}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#2c3e50", marginBottom: 14 }}>
                New Broker Account
              </div>
              <div style={addFormGrid}>
                <div style={fieldBox}>
                  <div style={fieldLabel}>Account Username *</div>
                  <input
                    type="text"
                    placeholder="e.g. ROTC1005"
                    value={newAcct.username}
                    onChange={e => setNewAcct(d => ({ ...d, username: e.target.value }))}
                    style={input} />
                </div>
                <div style={fieldBox}>
                  <div style={fieldLabel}>Password *</div>
                  <input
                    type="password"
                    value={newAcct.password}
                    onChange={e => setNewAcct(d => ({ ...d, password: e.target.value }))}
                    style={input} />
                </div>
                <div style={fieldBox}>
                  <div style={fieldLabel}>Broker *</div>
                  <select
                    value={newAcct.broker}
                    onChange={e => {
                      const broker = e.target.value;
                      setNewAcct(d => ({
                        ...d,
                        broker,
                        market: US_BROKERS.includes(broker) ? "US" : "IN",
                      }));
                    }}
                    style={input}>
                    {["angelone", "zerodha", "upstox", "alpaca", "tradier", "ibkr", "other"].map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div style={fieldBox}>
                  <div style={fieldLabel}>Market</div>
                  <div style={{
                    ...input,
                    background: "#f8f9fa",
                    color: US_BROKERS.includes(newAcct.broker) ? "#0070c9" : "#856404",
                    fontWeight: 600,
                    cursor: "default",
                  }}>
                    {US_BROKERS.includes(newAcct.broker) ? "US — United States" : "IN — India"}
                  </div>
                </div>
                <div style={fieldBox}>
                  <div style={fieldLabel}>API Key</div>
                  <input
                    type="text"
                    value={newAcct.api_key}
                    onChange={e => setNewAcct(d => ({ ...d, api_key: e.target.value }))}
                    style={{ ...input, fontFamily: "monospace" }} />
                </div>
                <div style={fieldBox}>
                  <div style={fieldLabel}>TOTP Token</div>
                  <input
                    type="text"
                    value={newAcct.totp_token}
                    onChange={e => setNewAcct(d => ({ ...d, totp_token: e.target.value }))}
                    style={{ ...input, fontFamily: "monospace" }} />
                </div>
              </div>
              {addError && (
                <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, background: "#fdecea", border: "1px solid #f5c6cb", color: "#721c24", fontSize: 13 }}>
                  {addError}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button onClick={addAccount} disabled={addSaving} style={btnPrimary}>
                  {addSaving ? "Saving…" : "Add Account"}
                </button>
                <button onClick={() => { setShowAddForm(false); setNewAcct(EMPTY_NEW); setAddError(null); }} style={btnSecondary}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────── */

function Card({ title, children }) {
  return (
    <div style={card}>
      <div style={cardHeader}><h5 style={cardTitle}>{title}</h5></div>
      <div style={cardBody}>{children}</div>
    </div>
  );
}

function Field({ label, editing, type = "text", options, value, display, onChange }) {
  return (
    <div style={fieldBox}>
      <div style={fieldLabel}>{label}</div>
      {editing ? (
        type === "select" ? (
          <select value={value} onChange={(e) => onChange(e.target.value)} style={input}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input type={type} value={value}
            onChange={(e) => onChange(e.target.value)} style={input} />
        )
      ) : (
        <div style={fieldValue}>{display || "—"}</div>
      )}
    </div>
  );
}

function SecretCell({ value, show, onToggle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: "monospace", fontSize: 12 }}>
        {value ? (show ? value : maskSecret(value)) : "—"}
      </span>
      {value && (
        <button onClick={onToggle} style={revealBtn}>{show ? "Hide" : "Show"}</button>
      )}
    </div>
  );
}

function ActionRow({ editing, saving, onEdit, onSave, onCancel }) {
  return (
    <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
      {editing ? (
        <>
          <button onClick={onSave} disabled={saving} style={btnPrimary}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button onClick={onCancel} style={btnSecondary}>Cancel</button>
        </>
      ) : (
        <button onClick={onEdit} style={btnSecondary}>Edit</button>
      )}
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div style={{ ...tile, borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#2c3e50" }}>{value ?? "—"}</div>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */

function maskSecret(val) {
  if (!val) return "—";
  if (val.length <= 4) return "****";
  return val.slice(0, 4) + "•".repeat(Math.max(0, val.length - 4));
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle = { fontSize: 22, fontWeight: 700, marginBottom: 20, color: "#2c3e50" };
const toast = {
  position: "fixed", top: 20, right: 20, zIndex: 9999,
  padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14
};
const summaryGrid = {
  display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24
};
const tile = { background: "#fff", borderRadius: 8, padding: "14px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };

const card = {
  background: "#fff", borderRadius: 12, marginBottom: 24,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)"
};
const cardHeader = { padding: "16px 20px", borderBottom: "1px solid #f0f0f0" };
const cardTitle = { margin: 0, fontSize: 15, fontWeight: 600, color: "#2c3e50" };
const cardBody = { padding: 20 };

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const fieldBox = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel = {
  fontSize: 12, fontWeight: 600, color: "#6c757d",
  textTransform: "uppercase", letterSpacing: "0.5px"
};
const fieldValue = { fontSize: 14, color: "#2c3e50", fontWeight: 500, padding: "6px 0" };
const input = {
  padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%"
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = {
  textAlign: "left", padding: "10px 12px", background: "#f8f9fa",
  borderBottom: "2px solid #dee2e6", fontWeight: 600, fontSize: 12,
  color: "#6c757d", textTransform: "uppercase"
};
const td = { padding: "10px 12px", borderBottom: "1px solid #f0f0f0", verticalAlign: "middle" };
const tr = { transition: "background 0.1s" };

const badge = {
  display: "inline-block", padding: "2px 8px", borderRadius: 10,
  background: "#e9ecef", fontSize: 12, color: "#495057", fontWeight: 600
};

const btnPrimary = {
  padding: "8px 18px", borderRadius: 8, border: "none",
  background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600
};
const btnSecondary = {
  padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc",
  background: "#fff", color: "#333", cursor: "pointer", fontSize: 13
};
const btnSmallPrimary = {
  padding: "4px 12px", borderRadius: 6, border: "none",
  background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 12
};
const btnSmallSecondary = {
  padding: "4px 12px", borderRadius: 6, border: "1px solid #ccc",
  background: "#fff", color: "#333", cursor: "pointer", fontSize: 12
};
const revealBtn = {
  padding: "2px 7px", borderRadius: 4, border: "1px solid #ddd",
  background: "#f8f9fa", cursor: "pointer", fontSize: 11, color: "#555"
};

const btnAddAccount = {
  padding: "8px 16px", borderRadius: 8, border: "1px dashed #007bff",
  background: "#f0f7ff", color: "#007bff", cursor: "pointer", fontSize: 13, fontWeight: 600
};
const addFormBox = {
  background: "#f8f9fb", borderRadius: 10, padding: "18px 20px", border: "1px solid #e8ecf0"
};
const addFormGrid = {
  display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14
};
